import { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectors } from 'darwin_core';
import { MODES } from 'darwin_core/utils/consts.js';

import ModalShell from '../components/ModalShell.jsx';
import { useZoneControls } from './useZoneControls.js';
import { isOverridden, overrideCopy } from './overrideCopy.js';

// The single exit from an override (OverrideNext / OverridePlus / DSR), surfaced
// at the top of the zone screen's action column. When the zone is not in an
// override it renders nothing.
//
// A zone can be pushed into an override from the mobile app or by the backend, so
// the exit is offered for both heating and hot-water zones — a cylinder stuck in a
// boost must never be a dead-end. It is also surfaced on the mode-select and
// Plus Hours screens (matching the app, which shows a cancel on those screens
// too).
//
// Reuses useZoneControls' cancelOverride dispatcher and the shared overrideCopy so
// the affordance names what it ends. Matching the app's CancelOverrideActionPanel,
// it is either SHOWN-AND-ENABLED or HIDDEN — never greyed out: it is hidden while
// the zone is offline (its command would be dropped, as the app hides it via
// isLoading/offline), and is NOT disabled in holiday or on an in-flight command. The
// app always lets you cancel an override the instant it is showing.
//
// Cancelling a DSR program goes through a confirmation first — the app's
// CancelDsrActiveModal, same title, wording and buttons — the other override types
// cancel directly.
export default function CancelOverride({ homeId, zoneId }) {
  const { online, sendable, actions } = useZoneControls(homeId, zoneId);
  const mode = useSelector((state) => selectors.getMode(state, homeId, zoneId));
  const isDhw = useSelector((state) => selectors.isDhw(state, homeId, zoneId));
  const [confirmingDsr, setConfirmingDsr] = useState(false);

  // Show-or-hide, never greyed (matches the app). Hidden while offline OR while the
  // socket is down — in both cases a cancel would be dropped, so never offer it.
  if (!isOverridden(mode, isDhw) || !online || !sendable) {
    return null;
  }

  // "Boost" is reserved for hot water; a heating zone's manual hold is "Plus Hours".
  const copy = overrideCopy(mode, isDhw);
  const isDsr = mode === MODES.DSRActive;

  return (
    <div className="cancel-override" aria-live="polite">
      <button
        type="button"
        className="cancel-override__button"
        onClick={() =>
          isDsr ? setConfirmingDsr(true) : actions.cancelOverride()
        }
      >
        {copy.cancel}
      </button>

      {confirmingDsr && (
        <ModalShell
          labelledBy="dsr-cancel-title"
          className="schedule-modal--confirm"
          onClose={() => setConfirmingDsr(false)}
        >
          <h2 id="dsr-cancel-title" className="schedule-modal__title">
            DSR Program Active
          </h2>
          <p className="schedule-modal__desc">
            Your heat pump is currently operating a DSR program. Cancelling this
            could affect your energy pricing.
          </p>
          <p className="schedule-modal__desc">
            Check with your energy provider for more details.
          </p>
          <p className="schedule-modal__desc">
            Do you wish to cancel the program?
          </p>
          <div className="schedule-modal__actions">
            <button
              type="button"
              className="schedule-modal__cancel"
              onClick={() => setConfirmingDsr(false)}
            >
              Back
            </button>
            <button
              type="button"
              className="schedule-modal__delete-btn"
              onClick={() => {
                actions.cancelOverride();
                setConfirmingDsr(false);
              }}
            >
              Cancel Program
            </button>
          </div>
        </ModalShell>
      )}
    </div>
  );
}
